import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { HelpHomeComponent } from './help-home/help-home.component';
import { HelpUserComponent } from './help-user/help-user.component';
import { HelpEventsComponent } from './help-event/help-events.component';
import { HelpResponderComponent } from './help-responder/help-responder.component';
import { HelpEvdComponent } from './help-evd/help-evd.component';
import { HelpAlarmsComponent } from './help-alarms/help-alarms.component';
import { HelpGuardsComponent } from './help-guards/help-guards.component';
import { HelpProfileComponent } from './help-profile/help-profile.component';
import { HelpSettingsComponent } from './help-settings/help-settings.component';
import { HelpStatisticsComponent } from './help-statistics/help-statistics.component';

interface Help {
  path: string;
  name: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-help',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    HelpHomeComponent,
    HelpUserComponent,
    HelpEventsComponent,
    HelpResponderComponent,
    HelpEvdComponent,
    HelpAlarmsComponent,
    HelpGuardsComponent,
    HelpProfileComponent,
    HelpSettingsComponent,
    HelpStatisticsComponent,
  ],
  templateUrl: './help.component.html',
  styleUrls: ['./help.component.css'],
})
export class HelpComponent implements OnInit, OnDestroy {
  currentRoute: string = '';

  contentRoute: string = '';

  search: boolean = true;

  /** Overview tiles (same tile UI as the /profile landing page). */
  readonly helpTiles: { route: string; icon: string; label: string }[] = [
    { route: '/home', icon: 'home', label: 'Startseite' },
    { route: '/user', icon: 'contacts', label: 'Kontakte' },
    { route: '/profile', icon: 'person', label: 'Profil' },
    { route: '/responder', icon: 'schedule', label: 'Responder' },
    { route: '/evd', icon: 'event_note', label: 'EvD' },
    { route: '/alarms', icon: 'fireplace', label: 'Einsatz' },
    { route: '/guards', icon: 'security', label: 'Sicherheitswachen' },
    { route: '/event', icon: 'calendar_today', label: 'Termine' },
    { route: '/statistics', icon: 'analytics', label: 'Auswertungen' },
  ];

  searchControl = new UntypedFormControl();

  sites: Help[] = [
    {
      path: '/home',
      name: 'Startseite',
    },
    {
      path: '/user',
      name: 'Mitglieder',
    },
    {
      path: '/responder',
      name: 'Responder',
    },
    {
      path: '/evd',
      name: 'EvD',
    },
    {
      path: '/event',
      name: 'Termine',
    },
    {
      path: '/alarms',
      name: 'Einsatz',
    },
    {
      path: '/guards',
      name: 'Wachen',
    },
    {
      path: '/profile',
      name: 'Profil',
    },
    {
      path: '/statistics',
      name: 'Auswertungen',
    },
    {
      path: '/settings',
      name: 'Einstellungen',
    },
  ];

  filteredSites!: Observable<Help[]>;

  private _router = inject(Router);

  ngOnDestroy(): void {
    this.contentRoute = this.currentRoute;
  }

  ngOnInit(): void {
    this.filteredSites = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value)),
    );
    this.loadHelp();
  }

  setHelpEntry(path: string): void {
    let contains = false;
    this.sites.forEach((site) => {
      if (path.startsWith(site.path)) contains = true;
    });
    if (contains) {
      this.contentRoute = '/' + path.split('#')[0].split('/')[1];
      this.disableSearch();
    } else {
      this.contentRoute = '';
      this.enableSearch();
    }
  }

  displayFn(help: Help): string {
    return help && help.name ? help.name : '';
  }

  private _filter(value: any): Help[] {
    if (value.name !== undefined) value = value.name;

    const filterValue = value.toLowerCase();

    return this.sites.filter(
      (option) => option.name.toLowerCase().indexOf(filterValue) === 0,
    );
  }

  public loadHelp() {
    this.currentRoute = this._router.url;
    this.setHelpEntry(this.currentRoute);
  }

  public disableSearch() {
    this.search = false;
  }

  public enableSearch() {
    this.search = true;
  }

  helpOverview() {
    this.contentRoute = '';
    this.enableSearch();
  }

  getSiteName(path: string): string {
    let returnvalue: string = '';
    this.sites.forEach((sites) => {
      if (path === sites.path) returnvalue = '- ' + sites.name;
    });
    return returnvalue;
  }
}
